import React, { useState, useEffect, useCallback } from 'react'
import { apiGet, apiPost, apiPut, apiDelete, getErrorMessage } from '../../utils/api'

/* ─── Types ─────────────────────────────────────────────────────────────── */

type ScheduleType = 'instant' | 'scheduled' | 'recurring'
interface RecurWindow { sd: number; st: string; ed: number; et: string }
interface Recurrence { tz: string; windows: RecurWindow[] }

interface WAGroupNotice {
  id: string
  name: string
  message: string
  group_ids: string[] | string // parsed array from API or JSON string
  is_active: number
  schedule_type: ScheduleType
  start_date: string | null
  end_date: string | null
  recurrence: Recurrence | string | null
  reply_once_per_sender: number
  cooldown_minutes: number
  replied_senders: Record<string, string> | string // parsed object from API or JSON string
  created_by: string | null
  created_at: string
  updated_at: string
}

interface WAGroup {
  id: string
  name: string
}

interface NoticeForm {
  name: string
  message: string
  group_ids: string[]
  schedule_type: ScheduleType
  start_date: string
  end_date: string
  recurrence: Recurrence
  reply_once_per_sender: boolean
  cooldown_minutes: number
}

const DEFAULT_TZ = 'Europe/London'

const EMPTY_FORM: NoticeForm = {
  name: '',
  message: '',
  group_ids: [],
  schedule_type: 'instant',
  start_date: '',
  end_date: '',
  recurrence: { tz: DEFAULT_TZ, windows: [] },
  reply_once_per_sender: true,
  cooldown_minutes: 60,
}

/* ─── Recurring-schedule helpers (independent of the widget-notice copy) ───── */

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const TZ_OPTIONS = ['Europe/London', 'Europe/Dublin', 'UTC', 'America/New_York', 'America/Los_Angeles', 'Australia/Sydney']

const SCHEDULE_PRESETS: { key: string; label: string; windows: RecurWindow[] }[] = [
  { key: 'weekend', label: 'Every weekend (Fri 5pm → Mon 9am)', windows: [{ sd: 5, st: '17:00', ed: 1, et: '09:00' }] },
  {
    key: 'weeknights', label: 'Weeknights (Mon–Fri 6pm → 8am)', windows: [
      { sd: 1, st: '18:00', ed: 2, et: '08:00' }, { sd: 2, st: '18:00', ed: 3, et: '08:00' },
      { sd: 3, st: '18:00', ed: 4, et: '08:00' }, { sd: 4, st: '18:00', ed: 5, et: '08:00' },
    ],
  },
  { key: 'weekends_full', label: 'All weekend (Sat & Sun, all day)', windows: [{ sd: 6, st: '00:00', ed: 1, et: '00:00' }] },
]

function to12h(hhmm: string): string {
  const [h, m] = (hhmm || '00:00').split(':').map(Number)
  const ap = h < 12 ? 'AM' : 'PM'
  const h12 = ((h + 11) % 12) + 1
  return `${h12}:${String(m || 0).padStart(2, '0')} ${ap}`
}

function summarizeWindows(windows: RecurWindow[]): string {
  if (!windows.length) return 'No windows yet — add one (or pick a preset) so the notice knows when to show.'
  return windows.map(w => `${DAY_NAMES[w.sd]} ${to12h(w.st)} → ${DAY_NAMES[w.ed]} ${to12h(w.et)}`).join(' · ')
}

function parseRecurrence(input: Recurrence | string | null): Recurrence {
  if (!input) return { tz: DEFAULT_TZ, windows: [] }
  let rec: any = input
  if (typeof input === 'string') { try { rec = JSON.parse(input) } catch { return { tz: DEFAULT_TZ, windows: [] } } }
  const windows = Array.isArray(rec?.windows) ? rec.windows.map((w: any) => ({ sd: Number(w.sd), st: String(w.st || '00:00'), ed: Number(w.ed), et: String(w.et || '00:00') })) : []
  return { tz: typeof rec?.tz === 'string' && rec.tz ? rec.tz : DEFAULT_TZ, windows }
}

// Client-side mirror of the server check so the list shows an accurate ACTIVE
// badge for recurring notices (the server is still the source of truth).
function weekMinutesInTz(date: Date, tz: string): number {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: false }).formatToParts(date)
  const get = (t: string) => parts.find(p => p.type === t)?.value || ''
  const dayMap: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }
  let hour = parseInt(get('hour'), 10) || 0; if (hour === 24) hour = 0
  return (dayMap[get('weekday')] ?? 0) * 1440 + hour * 60 + (parseInt(get('minute'), 10) || 0)
}
function isRecurringActiveNow(rec: Recurrence, now: Date = new Date()): boolean {
  if (!rec?.windows?.length) return false
  let nowMin: number
  try { nowMin = weekMinutesInTz(now, rec.tz || DEFAULT_TZ) } catch { nowMin = weekMinutesInTz(now, DEFAULT_TZ) }
  for (const w of rec.windows) {
    const toMin = (s: string) => { const m = /^(\d{1,2}):(\d{2})$/.exec(s || ''); return m ? (parseInt(m[1], 10) % 24) * 60 + (parseInt(m[2], 10) % 60) : NaN }
    const start = w.sd * 1440 + toMin(w.st), end = w.ed * 1440 + toMin(w.et)
    if (isNaN(start) || isNaN(end) || start === end) continue
    if (start < end) { if (nowMin >= start && nowMin < end) return true }
    else if (nowMin >= start || nowMin < end) return true
  }
  return false
}

/* ─── Icons ──────────────────────────────────────────────────────────────── */

const PlusIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
  </svg>
)

const PencilIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
  </svg>
)

const TrashIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
  </svg>
)

const XIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
)

const WhatsAppIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
)

/* ─── Component ──────────────────────────────────────────────────────────── */

export const WAGroupNotices: React.FC = () => {
  const [notices, setNotices] = useState<WAGroupNotice[]>([])
  const [groups, setGroups] = useState<WAGroup[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingNotice, setEditingNotice] = useState<WAGroupNotice | null>(null)
  const [form, setForm] = useState<NoticeForm>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [expandedNotice, setExpandedNotice] = useState<string | null>(null)

  // Fetch notices
  const fetchNotices = useCallback(async () => {
    try {
      const data = await apiGet<{ notices: WAGroupNotice[] }>('/wa-group-notices')
      setNotices(data.notices || [])
    } catch (err: any) {
      console.error('Failed to fetch WA group notices:', err)
      setError(getErrorMessage(err, 'An unexpected error occurred'))
    } finally {
      setLoading(false)
    }
  }, [])

  // Fetch available WhatsApp groups.
  // The /groups endpoint returns { success, data: [...] } (not { groups }), and a
  // live fetch can be slow or briefly return nothing while the gateway reconnects.
  // So mirror GroupsView: try live when we have a session, otherwise (or on
  // failure) fall back to the cached list so the picker still populates.
  const fetchGroups = useCallback(async () => {
    const normalise = (raw: any): WAGroup[] => {
      const list = Array.isArray(raw) ? raw : raw?.data ?? raw?.groups ?? []
      return (Array.isArray(list) ? list : [])
        .filter((g: any) => g && (g.id || g.jid))
        .map((g: any) => ({ id: g.id || g.jid, name: g.name || g.id || g.jid }))
    }
    const tryCached = async () => {
      try {
        const cached = await apiGet<any>('/integrations/whatsapp/groups/cached')
        const groups = normalise(cached)
        if (groups.length) setGroups(groups)
        return groups.length
      } catch {
        return 0
      }
    }
    try {
      const status = await apiGet<any>('/integrations/whatsapp/status')
      if (status.activeSession?.id) {
        const res = await apiGet<any>('/integrations/whatsapp/groups', { sessionId: status.activeSession.id })
        const groups = normalise(res)
        if (groups.length) {
          setGroups(groups)
          return
        }
      }
      // No session yet, or live fetch came back empty — use the cache.
      await tryCached()
    } catch {
      // Live status/groups failed — still try the cache.
      await tryCached()
    }
  }, [])

  useEffect(() => {
    fetchNotices()
    fetchGroups()
  }, [fetchNotices, fetchGroups])

  // Form handlers
  const openCreateForm = () => {
    setForm(EMPTY_FORM)
    setEditingNotice(null)
    setShowForm(true)
  }

  const openEditForm = (notice: WAGroupNotice) => {
    let groupIds: string[] = []
    try {
      groupIds = typeof notice.group_ids === 'string'
        ? JSON.parse(notice.group_ids || '[]')
        : (notice.group_ids || [])
    } catch { groupIds = [] }
    setForm({
      name: notice.name,
      message: notice.message,
      group_ids: groupIds,
      schedule_type: notice.schedule_type,
      start_date: notice.start_date || '',
      end_date: notice.end_date || '',
      recurrence: parseRecurrence(notice.recurrence),
      reply_once_per_sender: !!notice.reply_once_per_sender,
      cooldown_minutes: notice.cooldown_minutes,
    })
    setEditingNotice(notice)
    setShowForm(true)
  }

  const closeForm = () => {
    setShowForm(false)
    setEditingNotice(null)
    setForm(EMPTY_FORM)
  }

  const handleSave = async () => {
    if (!form.name.trim() || !form.message.trim()) {
      setError('Name and message are required')
      return
    }
    if (form.schedule_type === 'recurring' && form.recurrence.windows.length === 0) {
      setError('Add at least one recurring window (or pick a preset)')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const payload = {
        name: form.name.trim(),
        message: form.message.trim(),
        group_ids: form.group_ids,
        schedule_type: form.schedule_type,
        start_date: form.schedule_type === 'scheduled' ? form.start_date || null : null,
        end_date: form.schedule_type === 'scheduled' ? form.end_date || null : null,
        recurrence: form.schedule_type === 'recurring' ? form.recurrence : null,
        reply_once_per_sender: form.reply_once_per_sender,
        cooldown_minutes: form.cooldown_minutes,
      }
      if (editingNotice) {
        await apiPut(`/wa-group-notices/${editingNotice.id}`, payload)
      } else {
        await apiPost('/wa-group-notices', payload)
      }
      closeForm()
      fetchNotices()
    } catch (err: any) {
      setError(getErrorMessage(err, 'An unexpected error occurred'))
    } finally {
      setSaving(false)
    }
  }

  const handleToggle = async (notice: WAGroupNotice) => {
    try {
      await apiPost(`/wa-group-notices/${notice.id}/toggle`)
      fetchNotices()
    } catch (err: any) {
      setError(getErrorMessage(err, 'An unexpected error occurred'))
    }
  }

  const handleDelete = async (notice: WAGroupNotice) => {
    if (!window.confirm(`Delete notice "${notice.name}"? This cannot be undone.`)) return
    try {
      await apiDelete(`/wa-group-notices/${notice.id}`)
      fetchNotices()
    } catch (err: any) {
      setError(getErrorMessage(err, 'An unexpected error occurred'))
    }
  }

  const handleResetReplies = async (notice: WAGroupNotice) => {
    if (!window.confirm(`Reset all replied senders for "${notice.name}"? Everyone will get the auto-reply again.`)) return
    try {
      await apiPost(`/wa-group-notices/${notice.id}/reset-replies`)
      fetchNotices()
    } catch (err: any) {
      setError(getErrorMessage(err, 'An unexpected error occurred'))
    }
  }

  const toggleGroupSelection = (groupId: string) => {
    setForm(prev => ({
      ...prev,
      group_ids: prev.group_ids.includes(groupId)
        ? prev.group_ids.filter(id => id !== groupId)
        : [...prev.group_ids, groupId],
    }))
  }

  // Helpers
  const isActive = (notice: WAGroupNotice) => {
    if (!notice.is_active) return false
    if (notice.schedule_type === 'instant') return true
    if (notice.schedule_type === 'scheduled') {
      const now = new Date()
      if (notice.start_date && now < new Date(notice.start_date)) return false
      if (notice.end_date && now > new Date(notice.end_date)) return false
      return true
    }
    if (notice.schedule_type === 'recurring') return isRecurringActiveNow(parseRecurrence(notice.recurrence))
    return false
  }

  const getRepliedCount = (notice: WAGroupNotice) => {
    try {
      const senders = typeof notice.replied_senders === 'string'
        ? JSON.parse(notice.replied_senders || '{}')
        : (notice.replied_senders || {})
      return Object.keys(senders).length
    } catch { return 0 }
  }

  const getGroupNames = (notice: WAGroupNotice) => {
    try {
      const ids: string[] = typeof notice.group_ids === 'string'
        ? JSON.parse(notice.group_ids || '[]')
        : (notice.group_ids || [])
      if (ids.length === 0) return 'All groups'
      return ids.map(id => {
        const g = groups.find(gr => gr.id === id)
        return g ? g.name : id.replace(/@g\.us$/, '')
      }).join(', ')
    } catch { return 'All groups' }
  }

  const formatDate = (d: string | null) => {
    if (!d) return '—'
    return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        <span className="ml-2 text-txt-secondary">Loading notices...</span>
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-brand-600 dark:text-brand-400">
          <WhatsAppIcon />
          <h2 className="text-lg font-semibold text-txt-primary">WhatsApp Group Auto-Replies</h2>
        </div>
        <button
          onClick={openCreateForm}
          className="inline-flex items-center gap-2 px-3 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-medium transition-colors duration-150 shadow-sm"
        >
          <PlusIcon /> New Notice
        </button>
      </div>
      <p className="text-sm text-txt-secondary mb-4">
        Automatically reply to messages in WhatsApp groups when a notice is active (e.g. holiday closures, after-hours messages).
      </p>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 rounded-md bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-2 text-red-400 hover:text-red-600">
            <XIcon />
          </button>
        </div>
      )}

      {/* Empty state */}
      {notices.length === 0 && !showForm && (
        <div className="text-center py-12 bg-bg-secondary rounded-lg border border-border-primary">
          <div className="text-4xl mb-3">💬</div>
          <p className="text-txt-secondary mb-1">No WhatsApp group notices yet</p>
          <p className="text-txt-secondary text-sm mb-4">Create a notice to auto-reply in WhatsApp groups during holidays, after hours, etc.</p>
          <button
            onClick={openCreateForm}
            className="px-4 py-2.5 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-medium transition-colors duration-150 shadow-sm"
          >
            Create Your First Notice
          </button>
        </div>
      )}

      {/* Notices list */}
      {notices.length > 0 && (
        <div className="space-y-5">
          {notices.map(notice => {
            const active = isActive(notice)
            const repliedCount = getRepliedCount(notice)
            const expanded = expandedNotice === notice.id

            return (
              <div
                key={notice.id}
                className={`rounded-lg border transition-colors ${
                  active
                    ? 'border-green-300 dark:border-green-700 bg-green-50/50 dark:bg-green-900/10'
                    : 'border-border-primary bg-bg-secondary'
                }`}
              >
                {/* Summary row */}
                <div className="flex items-center gap-3 p-4">
                  {/* Toggle — matches Widget Notices style */}
                  <button
                    onClick={() => handleToggle(notice)}
                    style={{
                      marginTop: '2px',
                      position: 'relative',
                      display: 'inline-flex',
                      height: '24px',
                      width: '44px',
                      flexShrink: 0,
                      cursor: 'pointer',
                      borderRadius: '9999px',
                      border: '2px solid transparent',
                      transition: 'background-color 200ms',
                      backgroundColor: notice.is_active ? 'var(--color-brand-600, #2563eb)' : '#d1d5db',
                    }}
                    aria-pressed={!!notice.is_active}
                    role="switch"
                    title={notice.is_active ? 'Click to deactivate' : 'Click to activate'}
                  >
                    <span
                      style={{
                        pointerEvents: 'none',
                        display: 'inline-block',
                        height: '20px',
                        width: '20px',
                        borderRadius: '9999px',
                        backgroundColor: '#fff',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                        transition: 'transform 200ms',
                        transform: notice.is_active ? 'translateX(20px)' : 'translateX(0)',
                      }}
                    />
                  </button>

                  {/* Info */}
                  <div
                    className="flex-1 min-w-0 cursor-pointer"
                    onClick={() => setExpandedNotice(expanded ? null : notice.id)}
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-txt-primary truncate">{notice.name}</span>
                      {active && (
                        <span className="px-1.5 py-0.5 text-xs rounded-full bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 font-medium">
                          ACTIVE
                        </span>
                      )}
                      <span className={`px-1.5 py-0.5 text-xs rounded-full font-medium ${
                        notice.schedule_type === 'instant'
                          ? 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                          : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                      }`}>
                        {notice.schedule_type === 'scheduled' ? 'Date range' : notice.schedule_type === 'recurring' ? 'Recurring' : 'Instant'}
                      </span>
                    </div>
                    <p className="text-sm text-txt-secondary truncate mt-0.5">
                      {getGroupNames(notice)} · {repliedCount} replied
                      {notice.schedule_type === 'scheduled' && notice.start_date && (
                        <span> · {formatDate(notice.start_date)} — {formatDate(notice.end_date)}</span>
                      )}
                      {notice.schedule_type === 'recurring' && (
                        <span> · {summarizeWindows(parseRecurrence(notice.recurrence).windows)}</span>
                      )}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => openEditForm(notice)}
                      className="p-1.5 rounded-md text-txt-secondary hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                      title="Edit"
                    >
                      <PencilIcon />
                    </button>
                    <button
                      onClick={() => handleDelete(notice)}
                      className="p-1.5 rounded-md text-txt-secondary hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      title="Delete"
                    >
                      <TrashIcon />
                    </button>
                  </div>
                </div>

                {/* Expanded detail */}
                {expanded && (
                  <div className="px-4 pb-4 pt-0 border-t border-border-primary">
                    <div className="mt-3 space-y-2">
                      <div>
                        <span className="text-xs font-medium text-txt-secondary uppercase tracking-wide">Auto-Reply Message</span>
                        <div className="mt-1 p-3 bg-bg-primary rounded-md text-sm text-txt-primary whitespace-pre-wrap border border-border-primary">
                          {notice.message}
                        </div>
                      </div>
                      <div className="flex gap-6 text-sm text-txt-secondary">
                        <div>
                          <span className="font-medium">Cooldown:</span>{' '}
                          {notice.reply_once_per_sender ? 'Once per sender' : `${notice.cooldown_minutes} min`}
                        </div>
                        <div>
                          <span className="font-medium">Replied to:</span> {repliedCount} senders
                        </div>
                        <div>
                          <span className="font-medium">Created:</span> {formatDate(notice.created_at)}
                        </div>
                      </div>
                      {repliedCount > 0 && (
                        <button
                          onClick={() => handleResetReplies(notice)}
                          className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          Reset replied senders (allow re-sending)
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Create / Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in">
          <div className="bg-surface rounded-2xl shadow-xl w-full max-w-lg border border-border animate-slide-down max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <h3 className="text-lg font-semibold text-txt-primary">
                {editingNotice ? 'Edit Notice' : 'New WhatsApp Group Notice'}
              </h3>
              <button onClick={closeForm} className="p-1 rounded-lg text-txt-tertiary hover:text-txt-primary hover:bg-surface-hover transition-colors">
                <XIcon />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-5 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-txt-secondary mb-1.5">Notice Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g. Christmas Holiday Notice"
                  className="w-full px-3 py-2.5 rounded-lg bg-surface-secondary border border-border text-sm text-txt-primary outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 transition-colors"
                />
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-txt-secondary mb-1.5">Auto-Reply Message</label>
                <textarea
                  value={form.message}
                  onChange={e => setForm(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="e.g. Thanks for your message! Our office is closed for the holidays from Dec 25 – Jan 2. We'll get back to you when we return."
                  rows={3}
                  className="w-full px-3 py-2.5 rounded-lg bg-surface-secondary border border-border text-sm text-txt-primary outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 transition-colors resize-none"
                />
              </div>

              {/* Schedule Type */}
              <div>
                <label className="block text-sm font-medium text-txt-secondary mb-1.5">Activation Type</label>
                <div className="grid grid-cols-3 gap-2">
                  {([
                    ['instant', 'Instant'],
                    ['scheduled', 'Date range'],
                    ['recurring', 'Recurring'],
                  ] as [ScheduleType, string][]).map(([val, lbl]) => (
                    <button
                      key={val}
                      type="button"
                      onClick={() => setForm(prev => ({ ...prev, schedule_type: val }))}
                      className={`px-3 py-2.5 rounded-lg border text-sm font-medium text-center transition-all duration-150 ${
                        form.schedule_type === val
                          ? 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-600 dark:text-blue-300'
                          : 'bg-surface-secondary border-border text-txt-secondary hover:border-txt-tertiary'
                      }`}
                    >
                      {lbl}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-txt-tertiary mt-1.5">
                  {form.schedule_type === 'instant'
                    ? 'Active immediately when toggled on, until you turn it off.'
                    : form.schedule_type === 'scheduled'
                    ? 'Active between specific start and end dates (e.g. a holiday closure).'
                    : 'Active on a repeating weekly schedule — e.g. every weekend, or weeknights.'}
                </p>
              </div>

              {/* Date range (only for scheduled) */}
              {form.schedule_type === 'scheduled' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-txt-secondary mb-1.5">Start Date</label>
                    <input
                      type="date"
                      value={form.start_date}
                      onChange={e => setForm(prev => ({ ...prev, start_date: e.target.value }))}
                      className="w-full px-3 py-2.5 rounded-lg bg-surface-secondary border border-border text-sm text-txt-primary outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-txt-secondary mb-1.5">End Date</label>
                    <input
                      type="date"
                      value={form.end_date}
                      onChange={e => setForm(prev => ({ ...prev, end_date: e.target.value }))}
                      className="w-full px-3 py-2.5 rounded-lg bg-surface-secondary border border-border text-sm text-txt-primary outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 transition-colors"
                    />
                  </div>
                </div>
              )}

              {/* Recurring weekly windows */}
              {form.schedule_type === 'recurring' && (
                <div className="rounded-lg border border-border bg-surface-secondary p-3 space-y-3">
                  {/* Presets */}
                  <div>
                    <span className="block text-xs font-medium text-txt-secondary mb-1.5">Quick presets</span>
                    <div className="flex flex-wrap gap-1.5">
                      {SCHEDULE_PRESETS.map(p => (
                        <button
                          key={p.key}
                          type="button"
                          onClick={() => setForm(prev => ({ ...prev, recurrence: { ...prev.recurrence, windows: p.windows.map(w => ({ ...w })) } }))}
                          className="px-2.5 py-1 rounded-full border border-border text-xs font-medium text-txt-secondary hover:border-brand-400 hover:text-brand-600 transition-colors"
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Window rows */}
                  <div className="space-y-2">
                    {form.recurrence.windows.map((w, i) => (
                      <div key={i} className="flex flex-wrap items-center gap-1.5 text-sm">
                        <span className="text-txt-tertiary text-xs w-10">From</span>
                        <select
                          value={w.sd}
                          onChange={e => setForm(prev => { const ws = [...prev.recurrence.windows]; ws[i] = { ...ws[i], sd: Number(e.target.value) }; return { ...prev, recurrence: { ...prev.recurrence, windows: ws } } })}
                          className="px-2 py-1.5 rounded-md bg-surface border border-border text-sm text-txt-primary outline-none focus:border-brand-500"
                        >
                          {DAY_NAMES.map((d, di) => <option key={di} value={di}>{d}</option>)}
                        </select>
                        <input
                          type="time"
                          value={w.st}
                          onChange={e => setForm(prev => { const ws = [...prev.recurrence.windows]; ws[i] = { ...ws[i], st: e.target.value }; return { ...prev, recurrence: { ...prev.recurrence, windows: ws } } })}
                          className="px-2 py-1.5 rounded-md bg-surface border border-border text-sm text-txt-primary outline-none focus:border-brand-500"
                        />
                        <span className="text-txt-tertiary text-xs">until</span>
                        <select
                          value={w.ed}
                          onChange={e => setForm(prev => { const ws = [...prev.recurrence.windows]; ws[i] = { ...ws[i], ed: Number(e.target.value) }; return { ...prev, recurrence: { ...prev.recurrence, windows: ws } } })}
                          className="px-2 py-1.5 rounded-md bg-surface border border-border text-sm text-txt-primary outline-none focus:border-brand-500"
                        >
                          {DAY_NAMES.map((d, di) => <option key={di} value={di}>{d}</option>)}
                        </select>
                        <input
                          type="time"
                          value={w.et}
                          onChange={e => setForm(prev => { const ws = [...prev.recurrence.windows]; ws[i] = { ...ws[i], et: e.target.value }; return { ...prev, recurrence: { ...prev.recurrence, windows: ws } } })}
                          className="px-2 py-1.5 rounded-md bg-surface border border-border text-sm text-txt-primary outline-none focus:border-brand-500"
                        />
                        <button
                          type="button"
                          onClick={() => setForm(prev => ({ ...prev, recurrence: { ...prev.recurrence, windows: prev.recurrence.windows.filter((_, j) => j !== i) } }))}
                          className="p-1 rounded-md text-txt-tertiary hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                          title="Remove window"
                        >
                          <XIcon />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => setForm(prev => ({ ...prev, recurrence: { ...prev.recurrence, windows: [...prev.recurrence.windows, { sd: 5, st: '17:00', ed: 1, et: '09:00' }] } }))}
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-600 hover:underline"
                    >
                      <PlusIcon /> Add window
                    </button>
                  </div>

                  {/* Timezone + live summary */}
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-txt-secondary">Timezone</label>
                    <select
                      value={form.recurrence.tz}
                      onChange={e => setForm(prev => ({ ...prev, recurrence: { ...prev.recurrence, tz: e.target.value } }))}
                      className="px-2 py-1.5 rounded-md bg-surface border border-border text-sm text-txt-primary outline-none focus:border-brand-500"
                    >
                      {TZ_OPTIONS.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                    </select>
                  </div>
                  <div className="rounded-md bg-surface px-3 py-2 border border-border">
                    <span className="text-xs font-medium text-txt-secondary">Active </span>
                    <span className="text-xs text-txt-primary">{summarizeWindows(form.recurrence.windows)}</span>
                  </div>
                </div>
              )}

              {/* Target Groups */}
              <div>
                <label className="block text-sm font-medium text-txt-secondary mb-1.5">Target Groups</label>
                <p className="text-xs text-txt-tertiary mb-2">
                  Select which WhatsApp groups this notice applies to. Leave empty for all groups.
                </p>
                {groups.length > 0 ? (
                  <div className="max-h-40 overflow-y-auto rounded-lg border border-border bg-surface-secondary">
                    {groups.map(group => (
                      <label
                        key={group.id}
                        className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-surface-hover transition-colors border-b border-border last:border-b-0 ${
                          form.group_ids.includes(group.id) ? 'bg-blue-50 dark:bg-blue-900/10' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={form.group_ids.includes(group.id)}
                          onChange={() => toggleGroupSelection(group.id)}
                          className="rounded"
                        />
                        <span className="text-sm text-txt-primary truncate">{group.name}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="p-3 text-sm text-txt-tertiary bg-surface-secondary rounded-lg border border-border">
                    No WhatsApp groups found. Connect WhatsApp and join groups first.
                    <br />
                    <span className="text-xs">The notice will apply to all groups if none are selected.</span>
                  </div>
                )}
                {form.group_ids.length > 0 && (
                  <p className="mt-1 text-xs text-brand-600 dark:text-brand-400">
                    {form.group_ids.length} group{form.group_ids.length !== 1 ? 's' : ''} selected
                  </p>
                )}
              </div>

              {/* Reply behavior */}
              <div>
                <label className="block text-sm font-medium text-txt-secondary mb-1.5">Reply Behavior</label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.reply_once_per_sender}
                    onChange={e => setForm(prev => ({ ...prev, reply_once_per_sender: e.target.checked }))}
                    className="rounded"
                  />
                  <span className="text-sm text-txt-primary">Reply only once per sender</span>
                </label>
                {!form.reply_once_per_sender && (
                  <div className="mt-2 ml-6 flex items-center gap-2">
                    <label className="text-sm text-txt-secondary">Cooldown (minutes):</label>
                    <input
                      type="number"
                      value={form.cooldown_minutes}
                      onChange={e => setForm(prev => ({ ...prev, cooldown_minutes: Math.max(1, parseInt(e.target.value) || 1) }))}
                      min={1}
                      className="w-20 px-2 py-1.5 rounded-lg bg-surface-secondary border border-border text-sm text-txt-primary outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
              <button
                onClick={closeForm}
                className="px-4 py-2.5 rounded-lg text-sm font-medium text-txt-secondary hover:bg-surface-hover transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.name.trim() || !form.message.trim()}
                className="px-4 py-2.5 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-medium transition-colors duration-150 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : editingNotice ? 'Save Changes' : 'Create Notice'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
